import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../utility/useAuth";
import {
  currentRequestScope,
  getMe,
  isAccountUnavailable,
  onCapabilityRequired,
  RequestScope,
} from "../services/ApiService";
import { MeResponse } from "../types/MeResponse";
import { logger } from "../utility/logger";
import { identityOf, sameRequestScope } from "../services/requestScope";
import { CAPABILITY_KEYS, CapabilityKey, CapabilityMap } from "./capabilities";

/**
 * Where the provider is with `GET api/me` for the organisation the session acts in.
 *
 * - `loading`: no answer for this scope yet. Before licensing has passed, and again after an
 *   organisation switch, which drops the previous map. Screens wait; nothing is granted.
 * - `ready`: the answer for this scope is in use.
 * - `refreshing`: a `403 CAPABILITY_REQUIRED` asked for the answer again, in the same scope.
 *   The current map stays in use until the new one replaces it, so nothing unmounts.
 * - `failed`: the read did not work (a 503, a dead connection, a reply of the wrong shape).
 *   `retry()` reads again. Nothing is granted.
 * - `unusable`: `401 ACCOUNT_UNAVAILABLE`. The account cannot be used, and asking again
 *   changes nothing. Nothing is granted.
 *
 * Neither `loading` nor `failed` is "nothing granted" in the sense an Analytics-only user is:
 * they are "not known", and a screen must not treat them as an answer.
 */
export type AccessStatus = "loading" | "ready" | "refreshing" | "failed" | "unusable";

/** Who `GET api/me` says the session acts as. The flags are read through `can` only. */
export type ActingUser = Omit<MeResponse, "can">;

export interface AccessValue {
  status: AccessStatus;
  /** True only when the key is granted and the state is `ready` or `refreshing`. */
  can: (capability: CapabilityKey) => boolean;
  /** The acting user of the answer in use. Null while loading, failed or unusable. */
  acting: ActingUser | null;
  /**
   * The session asked to act as another account and the server did not honour it. The answer
   * is then the caller's own, in a site they asked to leave, so nothing is granted and the
   * screen says the switch failed. Set with `status: "ready"`, and kept through a refresh
   * (`refreshing`) that a `403 CAPABILITY_REQUIRED` starts from that answer.
   */
  switchFailed: boolean;
  /** The organisation `GET api/me` was asked for. Null until licensing has passed. */
  scope: RequestScope | null;
  /** Why the state is `failed` or `unusable`; null otherwise. */
  error: unknown;
  /** Read again after a failure. Does nothing in any other state. */
  retry: () => void;
  /**
   * Licensing has passed for this page load (PrivateRoute, on the cached path too). The first
   * call reads `GET api/me`. A later call reads it only when the session's scope has moved.
   */
  licensingPassed: () => void;
  /**
   * The org switcher has moved the session. A new scope drops the map and reads again, and so
   * does choosing again an organisation whose switch the server refused.
   */
  organizationSwitched: () => void;
}

interface AccessState {
  status: AccessStatus;
  scope: RequestScope | null;
  acting: ActingUser | null;
  // Null whenever nothing may be granted: loading, failed, unusable and a switch that failed.
  map: CapabilityMap | null;
  switchFailed: boolean;
  error: unknown;
}

const INITIAL: AccessState = {
  status: "loading",
  scope: null,
  acting: null,
  map: null,
  switchFailed: false,
  error: null,
};

/** One `GET api/me` and the scope it was asked in. Compared by identity, never by value. */
interface MeRequest {
  readonly scope: RequestScope;
}

export const sameScope = sameRequestScope;

/**
 * The 26 keys exactly, each true only when the server sent `true`. A key the server sends
 * and this client does not know is left out, because nothing here can ask for it.
 */
const mapOf = (can: MeResponse["can"]): CapabilityMap =>
  Object.freeze(
    Object.fromEntries(CAPABILITY_KEYS.map((key) => [key, can[key] === true]))
  ) as CapabilityMap;

const actingOf = (me: MeResponse): ActingUser => ({
  userCode: me.userCode,
  name: me.name,
  whoto: me.whoto,
  actingAs: me.actingAs,
  impersonating: me.impersonating,
  overrideHonoured: me.overrideHonoured,
});

const AccessContext = createContext<AccessValue | null>(null);

/**
 * Holds what the signed-in user may do in the organisation the session acts in, read from
 * `GET api/me`. Mounted once in `App.tsx`, above the route tree's `<Suspense>`, so neither a
 * navigation nor a lazy screen loading drops the answer.
 *
 * The map lives in this component's state and nowhere else: never in the persisted store, never
 * in `app_auth_cache`. Each page load reads it again.
 */
export const AccessProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  const [state, setState] = useState<AccessState>(INITIAL);

  // Read synchronously by the handlers below, which can run several times before React
  // renders: three refusals in one tick must see the first one's refresh.
  const statusRef = useRef<AccessStatus>(INITIAL.status);
  // The scope the provider follows: the one the last load was asked in.
  const scopeRef = useRef<RequestScope | null>(null);
  // The request whose answer may still be used. Any other answer is dropped, whatever it says.
  const pendingRef = useRef<MeRequest | null>(null);
  // The answer in use is a switch the server refused. Choosing that organisation again leaves
  // the scope where it was, and is still a request to try the switch again.
  const refusedRef = useRef(false);

  // The status is written to the ref here, at once: React may run an updater function only
  // when it next renders.
  const apply = useCallback(
    (status: AccessStatus, next: (prev: AccessState) => Omit<AccessState, "status">) => {
      statusRef.current = status;
      setState((prev) => ({ ...next(prev), status }));
    },
    []
  );

  const send = useCallback(
    (scope: RequestScope) => {
      const request: MeRequest = { scope };
      pendingRef.current = request;

      getMe(authRef.current, scope).then(
        (me) => {
          // A later request owns the state: a switch to another organisation, or a retry.
          if (pendingRef.current !== request) return;
          pendingRef.current = null;

          // An override that was asked for and not honoured is answered as the caller. That
          // map belongs to the caller, in a site they asked to leave, and is never used.
          const switchFailed = identityOf(scope) !== "" && !me.overrideHonoured;
          refusedRef.current = switchFailed;
          if (switchFailed) {
            logger.warn("The organisation switch was not honoured by the server.");
          }

          apply("ready", () => ({
            scope,
            acting: actingOf(me),
            map: switchFailed ? null : mapOf(me.can),
            switchFailed,
            error: null,
          }));
        },
        (error) => {
          if (pendingRef.current !== request) return;
          pendingRef.current = null;
          refusedRef.current = false;

          const unusable = isAccountUnavailable(error);
          logger.warn(
            unusable
              ? "This account cannot be used here."
              : "What this account may do could not be read.",
            error
          );
          apply(unusable ? "unusable" : "failed", () => ({
            scope,
            acting: null,
            map: null,
            switchFailed: false,
            error,
          }));
        }
      );
    },
    [apply]
  );

  /** Load for the session's scope: always when forced, otherwise only when it has moved. */
  const follow = useCallback(
    (force: boolean) => {
      const scope = currentRequestScope();
      if (!force && scopeRef.current && sameScope(scopeRef.current, scope)) return;

      scopeRef.current = scope;
      refusedRef.current = false;
      // The previous map is dropped in the same update that starts the read, so no render
      // shows one organisation's grants in another.
      apply("loading", () => ({ ...INITIAL, scope }));
      send(scope);
    },
    [apply, send]
  );

  const licensingPassed = useCallback(() => follow(false), [follow]);
  const organizationSwitched = useCallback(() => follow(refusedRef.current), [follow]);
  const retry = useCallback(() => {
    if (statusRef.current === "failed") follow(true);
  }, [follow]);

  useEffect(
    () =>
      onCapabilityRequired(() => {
        // Loading or refreshing: an answer is already on its way, and it is the one refresh
        // this burst of refusals gets. Failed or unusable: nothing is in use to refresh.
        if (statusRef.current !== "ready" || pendingRef.current || !scopeRef.current) return;

        const scope = scopeRef.current;
        apply("refreshing", (prev) => prev);
        send(scope);
      }),
    [apply, send]
  );

  const can = useCallback(
    (capability: CapabilityKey) => {
      if (state.status !== "ready" && state.status !== "refreshing") return false;
      const map = state.map;
      if (!map) return false;
      // Own keys only: a name such as "constructor" must not read through to the prototype.
      return Object.prototype.hasOwnProperty.call(map, capability) && map[capability] === true;
    },
    [state.status, state.map]
  );

  const value = useMemo<AccessValue>(
    () => ({
      status: state.status,
      can,
      acting: state.acting,
      switchFailed: state.switchFailed,
      scope: state.scope,
      error: state.error,
      retry,
      licensingPassed,
      organizationSwitched,
    }),
    [state, can, retry, licensingPassed, organizationSwitched]
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

/** The provider's value. Throws outside an `AccessProvider`, as `useAuth` does. */
export const useAccess = (): AccessValue => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return context;
};
